import {
  ArrowLeft,
  ArrowRight,
  ArrowsClockwise,
  FloppyDiskBack,
  GoogleLogo,
  Lightning,
  Link,
  MagnifyingGlass,
  Trash,
  TrendUp,
  Warning,
  X,
} from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { Button, Spinner, TextInput } from 'flowbite-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useBlocker } from 'react-router-dom';
import AnimatedModal from '../../../../components/AnimatedModal';
import AddIntegrationSkeleton from '../../../../components/SkeletonLoaders/AddIntegrationSkeleton';
import { useAuth, trackIncompleteIntegration, markIntegrationComplete } from '../../../../hooks';
import { useFetchSocialAccountStatus } from '../../../../hooks/post/useFetchSocialAccountStatus';
import { useInsertAppleIAP } from '../../../../hooks/post/useInsertAppleIAP';
import { useInsertAppleAds } from '../../../../hooks/post/useInsertAppleAds';
import { useInsertGoogleIAP } from '../../../../hooks/post/useInsertGoogleIAP';
import type { IIAPConnectResponse } from '../../../../interfaces';
import { useMetaAdsAuth } from '../../../../hooks/post/useMetaAdsAuth';
import { useMetaAdsCallback } from '../../../../hooks/post/useMetaAdsCallback';
import { useMetaAdsSaveAccount } from '../../../../hooks/post/useMetaAdsSaveAccount';
import { useDataSyncingWebsocket } from '../../../../hooks/useDataSyncingWebsocket';
import { useDeleteIntegration } from '../../../../hooks/delete/useDeleteIntegration';
import { convertAccountsToEntities } from '../../../../lib/utils';
import { httpService } from '../../../../services/httpService';
import {
  API_CONFIG,
  LOCAL_STORAGE_KEYS,
  PLATFORM_ICON_MAP,
  PLATFORM_LABEL_MAPPER,
  REVENUE_PLATFORMS,
  SPENDING_PLATFORMS,
} from '../../../../shared/constants';
import {
  AccountSelectionModal,
  AppleIAPModal,
  AppleAdsModal,
  GoogleIAPModal,
  SourceCard,
  SourceConfigurationBlock,
  StepIndicator,
} from '../components';
import {
  AppleIAPFormData,
  AppleAdsFormData,
  GoogleIAPFormData,
  IAccountsEntity,
  ICheckOnGoingIntegrationResponse,
  IInsertAppsResponse,
  IIntegrationsSet,
  IPlatformAccount,
  OAuthDetails,
  OAuthResponse,
  PlatformConfig,
  SelectedAccount,
} from '../interface';

dayjs.extend(utc);
dayjs.extend(timezone);

type PlatformType = 'google_ads' | 'google_admob' | 'apple_iap' | 'google_iap' | 'meta_ads' | 'apple_search_ads';

interface ApiError extends Error {
  response?: { data?: { message?: string } };
  data?: { email?: string };
}

function toApiError(err: unknown): ApiError {
  if (err instanceof Error) return err as ApiError;
  return Object.assign(new Error(String(err)));
}

interface IntegrationOperation {
  type: 'ADD' | 'DELETE';
  platform: PlatformType;
  source?: IIntegrationsSet; // For ADD
  email?: string; // For DELETE identification
  socialAccountId?: number | null; // For DELETE identification
}


interface AddIntegrationProps {
  isEditMode?: boolean;
  trackingId?: string;
  searchParams?: URLSearchParams;
  integratingPlatform?: 'google_ads' | 'google_admob' | 'meta_ads' | null;
}

const AddIntegration: React.FC<AddIntegrationProps> = ({
  isEditMode: propEditMode,
  trackingId: propTrackingId,
  searchParams: propSearchParams,
  integratingPlatform: propIntegratingPlatform,
}) => {
  const { t } = useTranslation();
  const { user, setUser } = useAuth();
  const { setTaskId } = useDataSyncingWebsocket({
    userId: user?.id?.toString(),
  });
  const location = useLocation();
  const navigate = useNavigate();
  const { trackingId: routeTrackingId } = useParams<{ trackingId: string }>();
  const queryClient = useQueryClient();
  const { mutateAsync: insertAppleIAP } = useInsertAppleIAP();
  const { mutateAsync: insertGoogleIAP } = useInsertGoogleIAP();
  const { mutateAsync: insertAppleAds } = useInsertAppleAds();
  const { mutateAsync: fetchSocialAccountStatus } = useFetchSocialAccountStatus();
  const deleteIntegrationMutation = useDeleteIntegration();
  const { mutateAsync: metaAdsAuth } = useMetaAdsAuth();
  const { mutateAsync: metaAdsCallback } = useMetaAdsCallback();
  const { mutateAsync: metaAdsSaveAccount } = useMetaAdsSaveAccount();


  // Wizard state
  const [currentStep, setCurrentStep] = useState<1 | 2>(() => {
    const sParams = propSearchParams || new URLSearchParams(window.location.search);
    if (sParams.get('code')) {
      const savedStep = localStorage.getItem(LOCAL_STORAGE_KEYS.WIZARD_STEP);
      if (savedStep === '2') return 2;
    }
    return 1;
  });
  const actualMode = useMemo(() => propEditMode ? 'edit' : 'create', [propEditMode]);
  const [trackingId, setTrackingId] = useState<string>('');
  const [integrationName, setIntegrationName] = useState('');
  const [integrationCreatedAt, setIntegrationCreatedAt] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  // Source states
  const [inward, setInward] = useState<IIntegrationsSet[]>([]);
  const [outward, setOutward] = useState<IIntegrationsSet[]>([]);

  // Modal states
  const [oauthModal, setOauthModal] = useState<{
    isOpen: boolean;
    platform: 'google_ads' | 'google_admob' | 'meta_ads';
    isAuthenticating: boolean;
  }>({
    isOpen: false,
    platform: 'google_ads',
    isAuthenticating: false,
  });

  const [accountModal, setAccountModal] = useState<{
    isOpen: boolean;
    platform: 'google_ads' | 'google_admob' | 'meta_ads' | 'apple_search_ads';
    accounts: IAccountsEntity[];
    selectedAccountId: string | null;
    sourceIndex: number | null;
    sourceType: 'inward' | 'outward' | null;
    isLoading: boolean;
    isConfirming: boolean;
  }>({
    isOpen: false,
    platform: 'google_ads',
    accounts: [],
    selectedAccountId: null,
    sourceIndex: null,
    sourceType: null,
    isLoading: false,
    isConfirming: false,
  });

  const [appleIAPModal, setAppleIAPModal] = useState<{ isOpen: boolean }>({ isOpen: false });

  const [googleIAPModal, setGoogleIAPModal] = useState<{ isOpen: boolean }>({ isOpen: false });

  const [appleAdsModal, setAppleAdsModal] = useState({
    isOpen: false,
    isSubmitting: false,
  });

  const [cancelModal, setCancelModal] = useState({
    isOpen: false,
    platform: '' as PlatformType,
    email: '',
    socialAccountId: null as number | null,
    isDeleting: false,
  });

  const [noAccountWarning, setNoAccountWarning] = useState<{
    isOpen: boolean;
    platform: 'google_ads' | 'google_admob' | 'meta_ads' | 'apple_search_ads';
    email?: string;
    warningType: 'no_platform_account' | 'no_sub_accounts' | 'missing_scope';
  }>({
    isOpen: false,
    platform: 'google_ads',
    warningType: 'no_platform_account',
  });

  const [isCancelConfirmModalOpen, setIsCancelConfirmModalOpen] = useState(false);
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] = useState(false);
  const [isOngoing, setIsOngoing] = useState(false);

  const [finalizeError, setFinalizeError] = useState<{
    isOpen: boolean;
    errors: { platform: string; message: string }[];
    isNetworkError: boolean;
  }>({ isOpen: false, errors: [], isNetworkError: false });

  const [operationsQueue, setOperationsQueue] = useState<IntegrationOperation[]>([]);

  // Derived state
  const isEditMode = useMemo(() => actualMode === 'edit', [actualMode]);
  const safeInward = useMemo(() => Array.isArray(inward) ? inward : [], [inward]);
  const safeOutward = useMemo(() => Array.isArray(outward) ? outward : [], [outward]);

  // Sync refs for stable callbacks
  const inwardRef = useRef(inward);
  const outwardRef = useRef(outward);

  useEffect(() => {
    inwardRef.current = inward;
  }, [inward]);

  useEffect(() => {
    outwardRef.current = outward;
  }, [outward]);

  const spendingConnected = safeInward.some((s) => s.is_active);
  const revenueConnected = safeOutward.some((s) => s.is_active);
  const step1Complete = spendingConnected || revenueConnected;

  // Track if there are unsaved changes
  const [initialName, setInitialName] = useState('');
  const isDirty = useMemo(() => {
    if (!isEditMode) return false;
    return operationsQueue.length > 0 || integrationName.trim() !== initialName.trim();
  }, [isEditMode, operationsQueue.length, integrationName, initialName]);

  const blocker = useBlocker(({ nextLocation }) => {
    return isDirty && !isSaving && nextLocation.pathname !== location.pathname;
  });

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setIsCancelConfirmModalOpen(true);
    }
  }, [blocker.state]);

  // Get summaries and accounts for display
  const spendingSummary = useMemo(() => {
    const connectedTypes = [...new Set(safeInward.filter(s => s.is_active).map(s => s.type))];
    if (connectedTypes.length === 0) return { name: t('configurations.integrations.spendingChannels'), icon: null };
    if (connectedTypes.length > 1) return { name: t('configurations.integrations.multipleChannels'), icon: null };

    const type = connectedTypes[0];
    return {
      name: PLATFORM_LABEL_MAPPER[type] || 'Spending Source',
      icon: PLATFORM_ICON_MAP[type] || null
    };
  }, [safeInward, t]);

  const revenueSummary = useMemo(() => {
    const connectedTypes = [...new Set(safeOutward.filter(s => s.is_active).map(s => s.type))];
    if (connectedTypes.length === 0) return { name: t('configurations.integrations.revenueSources'), icon: null };
    if (connectedTypes.length > 1) return { name: t('configurations.integrations.multipleRevenueSources'), icon: null };

    const type = connectedTypes[0];
    return {
      name: PLATFORM_LABEL_MAPPER[type] || 'Revenue Source',
      icon: PLATFORM_ICON_MAP[type] || null
    };
  }, [safeOutward, t]);

  const spendingAccounts: SelectedAccount[] = useMemo(() => {
    const accounts: SelectedAccount[] = [];

    safeInward.forEach(source => {
      // Google Ads
      if (source.type === 'google_ads' && source.selected_account_id) {
        const account = source.accounts?.find(a => String(a.id) === String(source.selected_account_id));
        if (account) {
          accounts.push({
            id: String(account.id),
            name: source.ads_sub_account_name || account.name || account.id,
            email: source.email,
            platform: 'google_ads',
            platformIcon: PLATFORM_ICON_MAP.google_ads,
            currencyCode: account.currencyCode || account.currency_code,
            timezone: account.reportingTimeZone || account.time_zone,
          });
        }
      }

      // Meta Ads
      if (source.type === 'meta_ads' && source.selected_account_id) {
        const account = source.accounts?.find(a => String(a.id) === String(source.selected_account_id));
        if (account) {
          accounts.push({
            id: String(account.id),
            name: source.ads_sub_account_name || account.name || account.id,
            email: source.email,
            platform: 'meta_ads',
            platformIcon: PLATFORM_ICON_MAP.facebook,
            currencyCode: (account).currency || account.currencyCode || account.currency_code,
            timezone: (account).timezone_str || account.reportingTimeZone || account.time_zone,
          });
        }
      }

      // Apple Search Ads
      if (source.type === 'apple_search_ads' && source.selected_account_id) {
        const account = (source.accounts)?.find((a) => String(a.id) === String(source.selected_account_id));
        accounts.push({
          id: String(source.selected_account_id),
          name: source.ads_sub_account_name || account?.name || source.selected_account_id,
          email: source.email,
          platform: 'apple_search_ads',
          platformIcon: PLATFORM_ICON_MAP.apple_ads,
          currencyCode: account?.currency_code,
          timezone: account?.reporting_time_zone,
        });
      }
    });

    return accounts;
  }, [safeInward]);

  const revenueAccounts: SelectedAccount[] = useMemo(() => {
    const accounts: SelectedAccount[] = [];

    safeOutward.forEach(source => {
      // Google AdMob
      if (source.type === 'google_admob' && source.admob_publisher_id) {
        const account = source.accounts?.find(acc => {
          const accId = acc.id || acc.publisher_id || acc.publisherId;
          return String(accId) === String(source.admob_publisher_id);
        });

        accounts.push({
          id: source.admob_publisher_id,
          name: source.user_name || source.admob_publisher_id,
          email: source.email,
          platform: 'google_admob',
          platformIcon: PLATFORM_ICON_MAP.google_admob,
          currencyCode: account?.currencyCode || account?.currency_code,
          timezone: account?.reportingTimeZone || account?.time_zone,
        });
      }

      // Apple IAP
      if (source.type === 'apple_iap' && source.apple_iap_vendor_number) {
        accounts.push({
          id: source.apple_iap_vendor_number,
          name: `Vendor ${source.apple_iap_vendor_number}`,
          email: source.email || `vendor-${source.apple_iap_vendor_number}@apple.com`,
          platform: 'apple_iap',
          platformIcon: PLATFORM_ICON_MAP.apple_iap,
          keyId: source.apple_iap_key_id || undefined,
          issuerId: source.apple_iap_issuer_id || undefined,
        });
      }

      // Google Play IAP
      if (source.type === 'google_iap' && source.google_iap_service_account_email) {
        accounts.push({
          id: String(source.id || 'google-play'),
          name: 'Google Play IAP',
          email: source.email,
          platform: 'google_iap',
          platformIcon: PLATFORM_ICON_MAP.google_iap,
          serviceAccount: source.google_iap_service_account_email || undefined,
        });
      }
    });

    return accounts;
  }, [safeOutward]);

  const areAllFinished = useMemo(() => {
    const allSources = [
      ...safeInward,
      ...safeOutward
    ];
    const pollableSources = allSources.filter(s => s.social_account_id);
    if (pollableSources.length === 0) return true;
    return pollableSources.every(s => s.status === 'finished');
  }, [safeInward, safeOutward]);

  const saveEditState = useCallback(() => {
    if (isEditMode) {
      const state = {
        inward,
        outward,
        operationsQueue,
        integrationName,
        currentStep,
      };
      localStorage.setItem(LOCAL_STORAGE_KEYS.EDIT_PROGRESS_STATE, JSON.stringify(state));
    }
  }, [isEditMode, inward, outward, operationsQueue, integrationName, currentStep]);

  const clearEditState = useCallback(() => {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.EDIT_PROGRESS_STATE);
  }, []);

  // API handlers
  const handleUpdateTrackingIntegration = useCallback(
    async ({
      inwards,
      outwards,
      id,
    }: {
      inwards: IIntegrationsSet[] | null;
      outwards: IIntegrationsSet[] | null;
      id?: string;
    }) => {
      if (!id) return null;

      if (inwards?.length === 0 && outwards?.length === 0) {
        setInward([]);
        setOutward([]);
        // window.location.pathname = "/integrations?trackingId=add";
        // navigate("/integrations?trackingId=add");
        // setTimeout(() => {
        //   window.location.reload();
        // }, 100);
        return null;
      } else {
        try {
          const data = await httpService.patch<ICheckOnGoingIntegrationResponse>(
            `${API_CONFIG.path.trackingIntegration}/${id}/update`,
            { inwards, outwards }
          );

          setInward(Array.isArray(data.inwards) ? data.inwards : []);
          setOutward(Array.isArray(data.outwards) ? data.outwards : []);

          // Invalidate queries but don't force refetch immediately to avoid flicker
          queryClient.invalidateQueries({ queryKey: ['integrations'] });
          return data;
        } catch (err: unknown) {
          toast.error(err instanceof Error ? err.message : t('configurations.integrations.update_error', 'Failed to update integration'));
          return null;
        }
      }

    },
    [queryClient, t]
  );

  const handleInitIntegrationData = useCallback(async () => {
    const sParams = propSearchParams || new URLSearchParams(location.search);
    const code = sParams.get('code');
    const trackingIdFromQuery = sParams.get('trackingId');
    const scope = sParams.get('scope');
    const scopeArray = scope?.split(' ');

    const initialTrackingId = propTrackingId || routeTrackingId || trackingIdFromQuery;
    const actualTrackingId = initialTrackingId;
    let emailFromCallback: string | undefined;

    try {
      let integration: ICheckOnGoingIntegrationResponse;

      if (actualTrackingId && actualTrackingId !== 'add') {
        // Fetch existing integration data
        integration = await httpService.get<ICheckOnGoingIntegrationResponse>(
          `${API_CONFIG.path.trackingIntegration}/${actualTrackingId}/details`
        );
      } else {
        // This case should now be handled by IntegrationResolver, but as a fallback
        // we'll just return if no trackingId is provided to avoid errors
        setIsLoading(false);
        return;
      }

      let currentInwards = Array.isArray(integration.inwards) ? integration.inwards : [];
      let currentOutwards = Array.isArray(integration.outwards) ? integration.outwards : [];

      setTrackingId(integration.tracking_id);
      setIntegrationName(integration.name || '');
      setInitialName(integration.name || '');

      setIntegrationCreatedAt(integration.created_at || '');
      setInward(currentInwards);
      setOutward(currentOutwards);
      setIsOngoing(!!integration.is_ongoing);

      // Restore edit stage if available
      const savedStateStr = localStorage.getItem(LOCAL_STORAGE_KEYS.EDIT_PROGRESS_STATE);
      if (savedStateStr && isEditMode) {
        try {
          const savedState = JSON.parse(savedStateStr);
          currentInwards = savedState.inward || [];
          currentOutwards = savedState.outward || [];
          setInward(currentInwards);
          setOutward(currentOutwards);
          setOperationsQueue(savedState.operationsQueue || []);
          setIntegrationName(savedState.integrationName || '');
          setCurrentStep(savedState.currentStep || 1);
          clearEditState();
        } catch (e) {
          console.error("Failed to restore saved state", e);
        }
      }

      // Track incomplete integration if it's still ongoing
      if (integration.is_ongoing && integration.created_at && (integration?.inwards !== null || integration?.outwards !== null)) {
        trackIncompleteIntegration(integration.tracking_id, integration.created_at);
      }

      if (code && integration) {
        const platform = propIntegratingPlatform || localStorage.getItem(LOCAL_STORAGE_KEYS.INTEGRATING_PLATFORM);

        const sParams = propSearchParams || new URLSearchParams(location.search);
        const state = sParams.get('state');
        const isMeta = platform === 'meta_ads';

        const response = (isMeta
          ? await metaAdsCallback({
            code,
            state: state || '',
            trackingId: isEditMode ? integration.tracking_id : undefined
          })
          : await httpService.post<OAuthResponse>(
            isMeta ? API_CONFIG.path.metaAdsCallback : API_CONFIG.path.googleOAuthCallback,
            {
              code,
              authorise_type: platform,
              scope: scopeArray,
              ...(isEditMode ? { tracking_id: integration.tracking_id } : {}),
            }
          )) as OAuthResponse;

        const isMetaPlatform = platform === 'meta_ads';
        const accounts = isMetaPlatform ? response.ad_accounts : response.accounts;
        const email = isMetaPlatform ? (isMeta ? response.user_info?.email : response.email) : response.email;
        const socialAccountId = isMetaPlatform ? response.social_account_id : response.google_social_account_id;

        if (accounts && accounts.length > 0) {
          const data = {
            email,
            admob_publisher_id: null,
            ads_parent_id: 0,
            ads_sub_account_id: null,
            currency: null,
            is_active: false,
            accounts,
            social_account_id: socialAccountId ? Number(socialAccountId) : undefined,
          };

          if (platform === 'google_ads') {
            // Ensure we are working with an array
            const safeInwards = Array.isArray(currentInwards) ? currentInwards : [];
            const updatedInwards = [...safeInwards];

            // In edit mode, if we're adding a new source, we should check for an existing entry that hasn't been configured yet (no selected_account_id)
            // OR if it's the same email and we're just refreshing it.
            let targetIdx = updatedInwards.findIndex(s => s.type === 'google_ads' && s.email === email && !s.selected_account_id);

            // If no "fresh" entry found, maybe we're refreshing an existing one? 
            // But we don't want to overwrite a VALID configured account unless it's the ONLY one.
            if (targetIdx === -1) {
              // If it's a completely new connection or we want to allow multiple, we push.
              // For now, let's stick to the user's requirement: preserve existing.
              targetIdx = updatedInwards.length;
              updatedInwards.push({ ...data, is_active: true, type: 'google_ads' } as IIntegrationsSet);
            } else {
              updatedInwards[targetIdx] = { ...updatedInwards[targetIdx], ...data, is_active: true };
            }

            setInward(updatedInwards);

            if (!isEditMode) {
              await handleUpdateTrackingIntegration({
                inwards: updatedInwards,
                outwards: currentOutwards,
                id: integration.tracking_id,
              });
            }

            setAccountModal({
              isOpen: true,
              platform: 'google_ads',
              accounts: convertAccountsToEntities(accounts),
              selectedAccountId: null,
              sourceIndex: targetIdx,
              sourceType: 'inward',
              isLoading: false,
              isConfirming: false,
            });
          } else if (platform === 'google_admob') {
            const safeOutwards = Array.isArray(currentOutwards) ? currentOutwards : [];
            const updatedOutwards = [...safeOutwards];

            let targetIdx = updatedOutwards.findIndex(s => s.type === 'google_admob' && s.email === email && !s.admob_publisher_id);

            if (targetIdx === -1) {
              targetIdx = updatedOutwards.length;
              updatedOutwards.push({ ...data, is_active: true, type: 'google_admob' } as IIntegrationsSet);
            } else {
              updatedOutwards[targetIdx] = { ...updatedOutwards[targetIdx], ...data, is_active: true };
            }

            setOutward(updatedOutwards);

            if (!isEditMode) {
              await handleUpdateTrackingIntegration({
                inwards: currentInwards,
                outwards: updatedOutwards,
                id: integration.tracking_id,
              });
            }

            setAccountModal({
              isOpen: true,
              platform: 'google_admob',
              accounts: convertAccountsToEntities(accounts),
              selectedAccountId: null,
              sourceIndex: targetIdx,
              sourceType: 'outward',
              isLoading: false,
              isConfirming: false,
            });
          } else if (platform === 'meta_ads') {
            const safeInwards = Array.isArray(currentInwards) ? currentInwards : [];
            const updatedInwards = [...safeInwards];

            let targetIdx = updatedInwards.findIndex(s => s.type === 'meta_ads' && s.email === email && !s.selected_account_id);

            if (targetIdx === -1) {
              targetIdx = updatedInwards.length;
              updatedInwards.push({ ...data, is_active: true, type: 'meta_ads' } as IIntegrationsSet);
            } else {
              updatedInwards[targetIdx] = { ...updatedInwards[targetIdx], ...data, is_active: true };
            }

            setInward(updatedInwards);

            if (!isEditMode) {
              await handleUpdateTrackingIntegration({
                inwards: updatedInwards,
                outwards: currentOutwards,
                id: integration.tracking_id,
              });
            }

            setAccountModal({
              isOpen: true,
              platform: 'meta_ads',
              accounts: convertAccountsToEntities(accounts),
              selectedAccountId: null,
              sourceIndex: targetIdx,
              sourceType: 'inward',
              isLoading: false,
              isConfirming: false,
            });
          }
        } else {
          setNoAccountWarning({
            isOpen: true,
            platform: platform as 'google_ads' | 'google_admob' | 'meta_ads',
            email: email,
            warningType: 'no_sub_accounts',
          });
        }

        // navigate(`/integrations?trackingId=${integration.tracking_id}`, { replace: true });
      }
    } catch (err: unknown) {
      const apiErr = toApiError(err);
      const errorMessage = apiErr.message || apiErr.response?.data?.message;

      if (errorMessage === "No AdMob account found" || errorMessage === "No Google Ads account found") {
        setNoAccountWarning({
          isOpen: true,
          platform: errorMessage.includes('Meta') ? 'meta_ads' : errorMessage.includes('AdMob') ? 'google_admob' : 'google_ads',
          email: apiErr.data?.email,
          warningType: 'no_platform_account',
        });

        // Ensure we stay on the integration page
        if (actualTrackingId && actualTrackingId === 'add') {
          navigate('/integrations', { replace: true });
        }
        return;
      }

      switch (errorMessage) {
        case 'MISSING_REQUIRED_GOOGLE_ADMOB_SCOPE':
        case 'MISSING_REQUIRED_GOOGLE_ADS_SCOPE':
          setNoAccountWarning({
            isOpen: true,
            platform: errorMessage.includes('ADMOB') ? 'google_admob' : 'google_ads',
            email: apiErr.data?.email || emailFromCallback,
            warningType: 'missing_scope',
          });
          return;
        default:
          toast.error(t('configurations.integrations.init_error', 'Failed to initialize integration'));
      }
      navigate('/integrations', { replace: true });
    } finally {
      setIsLoading(false);
    }
  }, [propSearchParams, location.search, propTrackingId, routeTrackingId, isEditMode, clearEditState, propIntegratingPlatform, metaAdsCallback, handleUpdateTrackingIntegration, navigate, t]);

  const fetchIntegrationStatus = useCallback(async () => {
    const allSources = [
      ...(Array.isArray(inwardRef.current) ? inwardRef.current : []),
      ...(Array.isArray(outwardRef.current) ? outwardRef.current : [])
    ];
    const socialAccountIds = allSources
      .map((s) => s.social_account_id)
      .filter((id): id is number => !!id);

    if (socialAccountIds.length === 0) return;

    try {
      const response = await fetchSocialAccountStatus({
        social_account_ids: socialAccountIds,
      });

      if (response?.data) {
        const statusMap = response.data;

        setInward((prev) =>
          prev.map((item) => {
            if (item.social_account_id && statusMap[item.social_account_id]) {
              return { ...item, status: statusMap[item.social_account_id] };
            }
            return item;
          })
        );

        setOutward((prev) =>
          prev.map((item) => {
            if (item.social_account_id && statusMap[item.social_account_id]) {
              return { ...item, status: statusMap[item.social_account_id] };
            }
            return item;
          })
        );
      }
    } catch (err) {
      console.error("Polling error:", err);
    }
  }, [fetchSocialAccountStatus]);

  useEffect(() => {
    handleInitIntegrationData();
  }, [handleInitIntegrationData]);

  // Polling for integration status
  const fetchStatusRef = useRef(fetchIntegrationStatus);
  useEffect(() => {
    fetchStatusRef.current = fetchIntegrationStatus;
  }, [fetchIntegrationStatus]);

  useEffect(() => {
    // Only poll when we have a trackingId, we're on Step 1, and we're not currently busy redirecting/loading
    if (!trackingId || currentStep !== 1 || isLoading || oauthModal.isAuthenticating || areAllFinished) return;

    // Initial fetch
    fetchStatusRef.current();

    const pollInterval = setInterval(() => {
      fetchStatusRef.current();
    }, 10000);

    return () => clearInterval(pollInterval);
  }, [trackingId, currentStep, isLoading, oauthModal.isAuthenticating, areAllFinished]); // Removed fetchIntegrationStatus from here

  const handleOpenSelectionModal = useCallback((source: IIntegrationsSet, platform: 'google_ads' | 'google_admob' | 'meta_ads') => {
    // Find index in appropriate array
    const type = platform === 'google_ads' ? 'inward' : 'outward';
    const list = type === 'inward' ? inwardRef.current : outwardRef.current;
    const index = list.findIndex(s => s === source);

    setAccountModal({
      isOpen: true,
      platform,
      accounts: convertAccountsToEntities(source.accounts || []),
      selectedAccountId: source.type === 'google_ads'
        ? (source.selected_account_id ? String(source.selected_account_id) : null)
        : (source.admob_publisher_id ? String(source.admob_publisher_id) : null),
      sourceIndex: index > -1 ? index : null,
      sourceType: type,
      isLoading: false,
      isConfirming: false,
    });
  }, []);

  // Connection handlers
  const handleConnect = async (platform: 'google_ads' | 'google_admob' | 'meta_ads') => {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.PLATFORM);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.ADD_ON);
    localStorage.setItem(LOCAL_STORAGE_KEYS.INTEGRATING_PLATFORM, platform);
    localStorage.setItem(LOCAL_STORAGE_KEYS.INTEGRATION_MODE, actualMode);
    localStorage.setItem(LOCAL_STORAGE_KEYS.TRACKING_ID, trackingId);
    localStorage.setItem(LOCAL_STORAGE_KEYS.WIZARD_STEP, String(currentStep));

    if (isEditMode) {
      saveEditState();
    }

    setOauthModal({ isOpen: false, platform, isAuthenticating: true });

    try {
      if (platform === 'meta_ads') {
        const details = await metaAdsAuth();
        window.location.href = details.authorization_url;
      } else {
        const endpoint = `${API_CONFIG.path.googleOAuth}/${platform}`;
        const details = await httpService.get<OAuthDetails>(endpoint);
        window.location.href = details.authorization_url;
      }
    } catch {
      toast.error(t('configurations.integrations.connection_error'));
      setOauthModal((prev) => ({ ...prev, isOpen: false, isAuthenticating: false }));
    }
  };

  const handleSourceConnect = (platform: PlatformConfig) => {
    if (platform.authType === 'oauth') {
      handleConnect(platform.id as 'google_ads' | 'google_admob' | 'meta_ads');
    } else if (platform.id === 'apple_iap') {
      setAppleIAPModal({ isOpen: true });
    } else if (platform.id === 'google_iap') {
      setGoogleIAPModal({ isOpen: true });
    } else if (platform.id === 'apple_search_ads') {
      setAppleAdsModal({ isOpen: true, isSubmitting: false });
    }
  };

  const handleSourceDisconnect = (source: IIntegrationsSet, platform: PlatformConfig) => {
    setCancelModal({
      isOpen: true,
      platform: platform.id as PlatformType,
      email: source.email || source.apple_iap_vendor_number || '',
      socialAccountId: source.social_account_id || null,
      isDeleting: false,
    });
  };

  const handleSourceSelection = (source: IIntegrationsSet, platformId: string) => {
    handleOpenSelectionModal(source, platformId as 'google_ads' | 'google_admob' | 'meta_ads');
  };

  const handleAccountSelect = (accountId: string) => {
    setAccountModal((prev) => ({
      ...prev,
      selectedAccountId: accountId,
    }));
  };

  const handleConfirmAccountSelection = async () => {
    const { platform, selectedAccountId } = accountModal;
    if (!selectedAccountId) return;
    setAccountModal((prev) => ({ ...prev, isConfirming: true }));

    try {
      if (platform === 'google_ads' || platform === 'meta_ads' || platform === 'apple_search_ads') {
        const updatedInward = [...safeInward];
        const targetIdx = accountModal.sourceIndex;

        if (targetIdx !== null && updatedInward[targetIdx]) {
          const accounts = Array.isArray(updatedInward[targetIdx].accounts) ? updatedInward[targetIdx].accounts : [];
          const selectedAccount = accounts?.find(acc => String(acc.id) === String(selectedAccountId));

          if (selectedAccount) {
            updatedInward[targetIdx] = {
              ...updatedInward[targetIdx],
              selected_account_id: String(selectedAccount.id),
              ads_sub_account_name: selectedAccount.name || selectedAccount.id
            };
          }
        }

        setInward(updatedInward);

        if (isEditMode) {
          const addedSource = targetIdx !== null ? updatedInward[targetIdx] : null;
          if (addedSource) {
            setOperationsQueue(prev => [...prev, { type: 'ADD', platform: platform as PlatformType, source: addedSource }]);
          }
        } else {
          const result = await handleUpdateTrackingIntegration({
            inwards: updatedInward,
            outwards: safeOutward,
            id: trackingId,
          });
          if (result?.created_at) {
            trackIncompleteIntegration(trackingId, result.created_at);
          } else if (integrationCreatedAt) {
            trackIncompleteIntegration(trackingId, integrationCreatedAt);
          }
        }
      } else {
        const updatedOutward = [...safeOutward];
        const targetIdx = accountModal.sourceIndex;

        if (targetIdx !== null && updatedOutward[targetIdx]) {
          const accounts = Array.isArray(updatedOutward[targetIdx].accounts) ? updatedOutward[targetIdx].accounts : [];
          const selectedAccount = accounts?.find(acc => {
            const accId = acc.id || acc.publisher_id || acc.publisherId;
            return String(accId) === String(selectedAccountId);
          });

          if (selectedAccount) {
            const pubId = selectedAccount.publisher_id || selectedAccount.publisherId || selectedAccount.id;
            updatedOutward[targetIdx] = {
              ...updatedOutward[targetIdx],
              admob_publisher_id: String(pubId),
              user_name: selectedAccount.name || selectedAccount.email || selectedAccount.publisher_name || pubId
            };
          }
        }

        setOutward(updatedOutward);

        if (isEditMode) {
          const addedSource = targetIdx !== null ? updatedOutward[targetIdx] : null;
          if (addedSource) {
            setOperationsQueue(prev => [...prev, { type: 'ADD', platform: 'google_admob', source: addedSource }]);
          }
        } else {
          const result = await handleUpdateTrackingIntegration({
            inwards: safeInward,
            outwards: updatedOutward,
            id: trackingId,
          });
          if (result?.created_at) {
            trackIncompleteIntegration(trackingId, result.created_at);
          } else if (integrationCreatedAt) {
            trackIncompleteIntegration(trackingId, integrationCreatedAt);
          }
        }
      }

      queryClient.invalidateQueries({ queryKey: ['integrations'] });

      setAccountModal({
        isOpen: false,
        platform: 'google_ads',
        accounts: [],
        selectedAccountId: null,
        sourceIndex: null,
        sourceType: null,
        isLoading: false,
        isConfirming: false,
      });

      toast.success(t('configurations.integrations.accountConnectedSuccess'));
    } catch (err: unknown) {
      toast.error(toApiError(err).message || t('configurations.integrations.saveAccountError'));
    } finally {
      setAccountModal((prev) => ({ ...prev, isConfirming: false }));
    }
  };

  const handleAppleAdsSubmit = async (formData: AppleAdsFormData) => {
    if (!trackingId) return;
    setAppleAdsModal((prev) => ({ ...prev, isSubmitting: true }));

    try {
      const response = await insertAppleAds({
        apple_ads_client_id: formData.client_id,
        apple_ads_team_id: formData.team_id,
        apple_ads_key_id: formData.key_id,
        tracking_id: trackingId,
      });

      const result = response;
      const orgs = result?.acl_response?.data || [];
      const socialAccountId = result?.social_account_id;

      if (orgs.length === 0) {
        setAppleAdsModal({ isOpen: false, isSubmitting: false });
        setNoAccountWarning({
          isOpen: true,
          platform: 'apple_search_ads',
          warningType: 'no_sub_accounts',
        });
        return;
      }

      // Map Apple Search Ads orgs to IAccountsEntity
      const accounts: IAccountsEntity[] = orgs.map((org) => ({
        id: String(org.orgId),
        name: org.displayName || org.orgName,
        currency_code: org.currency,
        reporting_time_zone: org.timeZone,
        social_account_id: socialAccountId,
      }));

      const newSource: IIntegrationsSet = {
        type: 'apple_search_ads',
        apple_ads_client_id: formData.client_id,
        apple_ads_team_id: formData.team_id,
        apple_ads_key_id: formData.key_id,
        is_active: true,
        category_type: 'inward',
        social_account_id: socialAccountId,
        accounts: accounts as IPlatformAccount[],
      };

      const updatedInward = [...safeInward];
      const existingIdx = updatedInward.findIndex((s) => s.type === 'apple_search_ads');
      let targetIdx: number;
      if (existingIdx > -1) {
        updatedInward[existingIdx] = { ...updatedInward[existingIdx], ...newSource };
        targetIdx = existingIdx;
      } else {
        targetIdx = updatedInward.length;
        updatedInward.push(newSource);
      }

      setInward(updatedInward);
      setAppleAdsModal({ isOpen: false, isSubmitting: false });

      if (!isEditMode) {
        await handleUpdateTrackingIntegration({
          inwards: updatedInward,
          outwards: safeOutward,
          id: trackingId,
        });
      }

      // Open account selection modal
      setAccountModal({
        isOpen: true,
        platform: 'apple_search_ads',
        accounts,
        selectedAccountId: null,
        sourceIndex: targetIdx,
        sourceType: 'inward',
        isLoading: false,
        isConfirming: false,
      });
    } catch (err: unknown) {
      toast.error(toApiError(err).message || 'Failed to connect Apple Search Ads');
    } finally {
      setAppleAdsModal((prev) => ({ ...prev, isSubmitting: false }));
    }
  };

  // ── Apple IAP callbacks (passed to AppleIAPModal) ──────────────────────
  const handleAppleIAPConnect = async (formData: AppleIAPFormData): Promise<IIAPConnectResponse> => {
    if (!trackingId || !formData.p8File) throw new Error('Missing trackingId or p8 file');

    const data = new FormData();
    data.append('p8_file', formData.p8File!, formData.p8File!.name);
    data.append('key_id', formData.keyId);
    data.append('issuer_id', formData.issuerId);
    data.append('vendor_number', formData.vendorNumber);
    data.append('tracking_id', trackingId);

    const appleRes = await insertAppleIAP({ formData: data });

    const newAppleIapSource: IIntegrationsSet = {
      type: 'apple_iap',
      apple_iap_vendor_number: formData.vendorNumber,
      apple_iap_key_id: formData.keyId,
      apple_iap_issuer_id: formData.issuerId,
      is_active: true,
      category_type: 'outward',
      social_account_id: appleRes.social_account_id,
    };

    const updatedOutward = [...safeOutward];
    const existingIdx = updatedOutward.findIndex(s => s.type === 'apple_iap' && s.apple_iap_vendor_number === formData.vendorNumber);
    if (existingIdx > -1) {
      updatedOutward[existingIdx] = { ...updatedOutward[existingIdx], ...newAppleIapSource };
    } else {
      updatedOutward.push(newAppleIapSource);
    }
    setOutward(updatedOutward);

    if (isEditMode) {
      setOperationsQueue(prev => [...prev, { type: 'ADD', platform: 'apple_iap', source: newAppleIapSource }]);
    } else {
      const result = await handleUpdateTrackingIntegration({ inwards: safeInward, outwards: updatedOutward, id: trackingId });
      if (result?.created_at) trackIncompleteIntegration(trackingId, result.created_at);
      else if (integrationCreatedAt) trackIncompleteIntegration(trackingId, integrationCreatedAt);
    }

    return appleRes;
  };

  const handleAppleIAPSuccess = () => {
    setAppleIAPModal({ isOpen: false });
    queryClient.invalidateQueries({ queryKey: ['integrations'] });
    toast.success(t('configurations.integrations.appleIapConnectSuccess'));
  };

  // ── Google IAP callbacks (passed to GoogleIAPModal) ─────────────────────
  const handleGoogleIAPConnect = async (formData: GoogleIAPFormData): Promise<IIAPConnectResponse> => {
    if (!trackingId || !formData.service_account_json?.length) throw new Error('Missing trackingId or JSON file');

    const data = new FormData();
    data.append('service_account_json', formData.service_account_json![0]);
    data.append('email', formData.email);
    data.append('bucket_name', formData.bucket_name);
    data.append('tracking_id', trackingId);

    const googleRes = await insertGoogleIAP({ formData: data });

    const newGoogleIapSource: IIntegrationsSet = {
      type: 'google_iap',
      google_iap_service_account_email: formData.email,
      google_iap_bucket_name: formData.bucket_name,
      is_active: true,
      category_type: 'outward',
      email: formData.email,
      social_account_id: googleRes.social_account_id,
    };

    const updatedOutward = [...safeOutward];
    const existingIdx = updatedOutward.findIndex(s => s.type === 'google_iap' && s.email === formData.email);
    if (existingIdx > -1) {
      updatedOutward[existingIdx] = { ...updatedOutward[existingIdx], ...newGoogleIapSource };
    } else {
      updatedOutward.push(newGoogleIapSource);
    }
    setOutward(updatedOutward);

    if (isEditMode) {
      setOperationsQueue(prev => [...prev, { type: 'ADD', platform: 'google_iap', source: newGoogleIapSource }]);
    } else {
      const result = await handleUpdateTrackingIntegration({ inwards: safeInward, outwards: updatedOutward, id: trackingId });
      if (result?.created_at) trackIncompleteIntegration(trackingId, result.created_at);
      else if (integrationCreatedAt) trackIncompleteIntegration(trackingId, integrationCreatedAt);
    }

    return googleRes;
  };

  const handleGoogleIAPSuccess = () => {
    setGoogleIAPModal({ isOpen: false });
    queryClient.invalidateQueries({ queryKey: ['integrations'] });
    toast.success(t('configurations.integrations.googleIapConnectSuccess'));
  };

  const handleUpdateIntegrationName = async (id: string, newName: string) => {
    try {
      await httpService.patch(
        `${API_CONFIG.path.trackingIntegration}/${id}/update`,
        { name: newName }
      );
      toast.success(t("configurations.integrations.nameUpdated", "Integration name updated"));
      queryClient.invalidateQueries({ queryKey: ["integrations"] });
    } catch (error) {
      toast.error(t("configurations.integrations.nameUpdateError", "Failed to update integration name"));
      throw error;
    }
  };

  const handleFinalize = async () => {
    if (!integrationName.trim()) {
      toast.error(t('configurations.integrations.enterIntegrationName'));
      return;
    }

    setIsSaving(true);
    setFinalizeError({ isOpen: false, errors: [], isNetworkError: false });

    try {
      // 1. Process all deferred registration APIs or operations queue
      const errors: { platform: string; message: string }[] = [];

      if (isEditMode && operationsQueue.length > 0) {
        // Sequential execution of tracked operations
        for (const op of operationsQueue) {
          try {
            if (op.type === 'DELETE') {
              if (op.socialAccountId) {
                await httpService.delete(`${API_CONFIG.path.deleteSocialAccount}/${op.socialAccountId}`);
              }
            } else if (op.type === 'ADD' && op.source) {
              const s = op.source;
              if (s.type === 'google_ads' && s.selected_account_id) {
                await httpService.post(API_CONFIG.path.googleAdsSaveAccount, {
                  merchant_customer_id: [s.selected_account_id],
                  tracking_id: trackingId,
                });
              } else if (s.type === 'meta_ads' && s.selected_account_id) {
                await metaAdsSaveAccount({
                  account_id: s.selected_account_id,
                  social_account_id: s.social_account_id!,
                  ad_account_name: s.ads_sub_account_name || s.selected_account_id,
                  tracking_id: trackingId,
                });
              } else if (s.type === 'apple_search_ads' && s.selected_account_id) {
                const selectedAccount = s.accounts?.find((a: IPlatformAccount) => String(a.id) === String(s.selected_account_id));
                if (selectedAccount) {
                  await httpService.post(API_CONFIG.path.activateAppleAds, {
                    social_account_id: s.social_account_id,
                    org_id: s.selected_account_id,
                    org_name: selectedAccount.name,
                    currency: selectedAccount.currency_code || selectedAccount.currencyCode || 'USD',
                    time_zone: selectedAccount.reporting_time_zone || selectedAccount.reportingTimeZone || selectedAccount.time_zone || 'UTC'
                  });
                }
              } else if (s.type === 'google_admob' && s.admob_publisher_id) {
                const response = await httpService.get<IInsertAppsResponse>(
                  API_CONFIG.path.insertApps,
                  {
                    publisher_ids: s.admob_publisher_id,
                    tracking_id: trackingId,
                  }
                );
                if (response?.task_id) {
                  setTaskId(response.task_id);
                  localStorage.setItem(LOCAL_STORAGE_KEYS.TASK_ID, response.task_id);
                }
              } else if (s.type === 'apple_iap' || s.type === 'google_iap') {
                queryClient.invalidateQueries({ queryKey: ['integrations'] });
              }
            }
          } catch (err: unknown) {
            errors.push({
              platform: PLATFORM_LABEL_MAPPER[op.platform] || op.platform,
              message: toApiError(err).message || `Failed to ${op.type.toLowerCase()} ${op.platform} source`
            });
          }
        }
      } else if (!isEditMode) {
        // Original logic for new integrations
        // Google Ads
        for (const s of safeInward) {
          if (s.type === 'google_ads' && s.selected_account_id && s.is_active) {
            try {
              await httpService.post(API_CONFIG.path.googleAdsSaveAccount, {
                merchant_customer_id: [s.selected_account_id],
                // tracking_id omitted for Add mode
              });
            } catch (err: unknown) {
              errors.push({ platform: 'Google Ads', message: toApiError(err).message || t('configurations.integrations.errors.saveGoogleAds', 'Failed to save Google Ads account') });
            }
          }
        }

        // Meta Ads
        for (const s of safeInward) {
          if (s.type === 'meta_ads' && s.selected_account_id && s.is_active) {
            try {
              await metaAdsSaveAccount({
                account_id: s.selected_account_id,
                social_account_id: s.social_account_id!,
                ad_account_name: s.ads_sub_account_name || s.selected_account_id,
                tracking_id: trackingId,
              });
            } catch (err: unknown) {
              errors.push({ platform: 'Meta Ads', message: toApiError(err).message || t('configurations.integrations.errors.saveMetaAds', 'Failed to save Meta Ads account') });
            }
          }
        }

        // Apple Search Ads
        for (const s of safeInward) {
          if (s.type === 'apple_search_ads' && s.selected_account_id && s.is_active) {
            try {
              const selectedAccount = s.accounts?.find((a: IPlatformAccount) => String(a.id) === String(s.selected_account_id));
              if (selectedAccount) {
                await httpService.post(API_CONFIG.path.activateAppleAds, {
                  social_account_id: s.social_account_id,
                  org_id: s.selected_account_id,
                  org_name: selectedAccount.name,
                  currency: selectedAccount.currency_code || selectedAccount.currencyCode || 'USD',
                  time_zone: selectedAccount.reporting_time_zone || selectedAccount.reportingTimeZone || selectedAccount.time_zone || 'UTC'
                });
              }
            } catch (err: unknown) {
              errors.push({ platform: 'Apple Search Ads', message: toApiError(err).message || t('configurations.integrations.errors.saveAppleAds', 'Failed to save Apple Search Ads account') });
            }
          }
        }

        // AdMob
        for (const s of safeOutward) {
          if (s.type === 'google_admob' && s.admob_publisher_id && s.is_active) {
            try {
              const response = await httpService.get<IInsertAppsResponse>(
                API_CONFIG.path.insertApps,
                {
                  publisher_ids: s.admob_publisher_id,
                  // tracking_id omitted for Add mode
                }
              );
              if (response?.task_id) {
                setTaskId(response.task_id);
                localStorage.setItem(LOCAL_STORAGE_KEYS.TASK_ID, response.task_id);
              }
            } catch (err: unknown) {
              errors.push({ platform: 'Google AdMob', message: toApiError(err).message || 'Failed to insert AdMob apps' });
            }
          }

          if ((s.type === 'apple_iap' || s.type === 'google_iap') && s.is_active) {
            queryClient.invalidateQueries({ queryKey: ['integrations'] });
          }
        }
      }

      // 1.5 Sync tracking integration state if anything changed
      if (isEditMode) {
        await handleUpdateTrackingIntegration({
          inwards: inward,
          outwards: outward,
          id: trackingId,
        });
      }

      if (errors.length > 0) {
        setFinalizeError({
          isOpen: true,
          errors,
          isNetworkError: errors.some(e => e.message.toLowerCase().includes('network') || e.message.toLowerCase().includes('fetch')),
        });
        setIsSaving(false);
        return;
      }

      // 2. Final save/update
      localStorage.setItem(LOCAL_STORAGE_KEYS.SHOW_SYNC_PROGRESS, 'true');
      localStorage.removeItem(LOCAL_STORAGE_KEYS.INTEGRATING_PLATFORM);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.INTEGRATION_MODE);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.WIZARD_STEP);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.TRACKING_ID);

      if (isEditMode) {
        await handleUpdateIntegrationName(trackingId, integrationName.trim());
        clearEditState();
      } else {
        await httpService.patch<ICheckOnGoingIntegrationResponse>(
          `${API_CONFIG.path.trackingIntegration}/${trackingId}/save`,
          { isOngoing: false, name: integrationName }
        );
      }

      // Mark integration as complete in localStorage
      markIntegrationComplete();

      // Send completion confirmation email (non-blocking)
      // if (!isEditMode) {
      //   httpService.post(
      //     API_CONFIG.path.sendIntegrationCompletionConfirmation,
      //     { tracking_id: trackingId }
      //   ).catch((err) => {
      //     console.error('Failed to send completion confirmation email:', err);
      //   });
      // }

      // Fetch latest user to update permissions/state
      const user = await httpService.getCurrentUser();
      if (user) {
        httpService.setUserData(user);
        setUser(user);
        queryClient.invalidateQueries({ queryKey: ['integrations'] });
        navigate('/integration-pairs');
        toast.success(isEditMode ? t('configurations.integrations.integrationUpdatedSuccess') : t('configurations.integrations.integrationCreatedSuccess'));
      }
    } catch (err: unknown) {
      toast.error(toApiError(err).message || t('configurations.integrations.saveIntegrationError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteSource = async () => {
    const { platform, email, socialAccountId } = cancelModal;
    if (!trackingId || !platform) return;

    setCancelModal((prev) => ({ ...prev, isDeleting: true }));

    try {
      // 2. Update local state and tracking integration
      let updatedInward = [...safeInward];
      let updatedOutward = [...safeOutward];

      if (platform === 'google_ads') {
        updatedInward = updatedInward.filter(s => !(s.type === platform && s.email === email));
      } else {
        updatedOutward = updatedOutward.filter(s => !(s.type === platform && (s.email === email || s.apple_iap_vendor_number === email)));

      }

      setInward(updatedInward);
      setOutward(updatedOutward);

      if (isEditMode) {
        setOperationsQueue(prev => [...prev, { type: 'DELETE', platform, email, socialAccountId }]);
      } else {
        if (socialAccountId) {
          httpService.delete(`${API_CONFIG.path.deleteSocialAccount}/${socialAccountId}`).then(() => {
            handleUpdateTrackingIntegration({
              inwards: updatedInward,
              outwards: updatedOutward,
              id: trackingId,
            });
          }).catch((err: unknown) => {
            throw new Error(toApiError(err).message || t('configurations.integrations.disconnectSourceError'));
          });
        } else {
          toast.error(t('configurations.integrations.socialAccountNotFound'));
          return;
        }
      }

      setCancelModal({ isOpen: false, platform: '' as PlatformType, email: '', socialAccountId: null, isDeleting: false });
      toast.success(t('configurations.integrations.sourceDisconnectedSuccess'));
    } catch (err: unknown) {
      toast.error(toApiError(err).message || t('configurations.integrations.disconnectSourceError'));
    } finally {
      setCancelModal((prev) => ({ ...prev, isDeleting: false }));
    }
  };

  const handleCancelIntegration = async () => {
    if (!trackingId) return;
    setIsCancelling(true);
    try {
      await httpService.delete(`${API_CONFIG.path.trackingIntegration}/${trackingId}/delete-all`);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.INTEGRATION_MODE);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.WIZARD_STEP);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.TRACKING_ID);
      toast.success(t('configurations.integrations.integrationCancelledSuccess'));
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      // Mark integration as complete when integration is cancelled
      markIntegrationComplete();
      navigate('/integration-pairs');
    } catch (err: unknown) {
      toast.error(toApiError(err).message || t('configurations.integrations.cancelIntegrationError'));
    } finally {
      setIsCancelling(false);
    }
  };

  const handleDeleteIntegrationPair = async () => {
    if (!trackingId) return;
    setIsCancelling(true);
    try {
      if (isOngoing) {
        await httpService.delete(`${API_CONFIG.path.trackingIntegration}/${trackingId}/delete-all`);
        toast.success(t("configurations.integrations.ongoingIntegrationDeleted", "Ongoing integration deleted successfully"));
      } else {
        await deleteIntegrationMutation.mutateAsync(trackingId);
        toast.success(t("configurations.integrations.integrationDeleted", "Integration deleted successfully"));
      }
      localStorage.removeItem(LOCAL_STORAGE_KEYS.INTEGRATION_MODE);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.WIZARD_STEP);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.TRACKING_ID);
      queryClient.invalidateQueries({ queryKey: ["integrations"] });
      navigate('/integration-pairs');
      setIsDeleteConfirmModalOpen(false);
    } catch (error) {
      console.error("Failed to delete integration", error);
      toast.error(t("configurations.integrations.deleteIntegrationError", "Failed to delete integration"));
    } finally {
      setIsCancelling(false);
    }
  };

  if (isLoading) {
    return <AddIntegrationSkeleton />;
  }

  return (
    <div className='relative min-h-full'>
      {/* Header */}
      <div className='mb-6'>
        <div className="flex items-center gap-2">
          <GoogleLogo size={20} weight="duotone" className="text-gray-700" />
          <h1 className="text-md font-bold text-gray-700">
            {isEditMode ? t('configurations.integrations.editIntegration') : t("configurations.integrations.add_new_integration")}
          </h1>
        </div>
        <div className="text-xs text-gray-600 transition-all duration-300 ">
          {currentStep === 1 ? <span key={currentStep} className='animate-fade-in'>{t('configurations.integrations.step1Desc')}</span> : <span key={currentStep} className='animate-fade-in'>{t('configurations.integrations.step2Desc')}</span>}
        </div>
      </div>

      {/* Step Indicator */}
      <div className="flex justify-center mb-10">
        <StepIndicator currentStep={currentStep} />
      </div>

      {/* Step 1: Connect Sources */}
      {currentStep === 1 && (
        <div className="space-y-8">
          <div className="grid md:grid-cols-2 gap-2">
            <SourceConfigurationBlock
              title={t("configurations.integrations.spendingChannelsTitle", "Spending Channels")}
              titleColorClass="text-indigo-600"
              icon={<Lightning weight="duotone" size={16} />}
              iconContainerClass="bg-indigo-50"
              badgeColor="indigo"
              platforms={SPENDING_PLATFORMS}
              connectedSources={safeInward}
              onConnect={handleSourceConnect}
              onDisconnect={handleSourceDisconnect}
              onSelectAccount={handleSourceSelection}
              isAuthenticating={oauthModal.isAuthenticating}
              authenticatingPlatform={oauthModal.platform}
              isEditMode={isEditMode}
            />

            <SourceConfigurationBlock
              title={t("configurations.integrations.revenueSourcesTitle", "Revenue Sources")}
              titleColorClass="text-emerald-600"
              icon={<TrendUp weight="duotone" size={16} />}
              iconContainerClass="bg-emerald-50"
              badgeColor="green"
              platforms={REVENUE_PLATFORMS}
              connectedSources={safeOutward}
              onConnect={handleSourceConnect}
              onDisconnect={handleSourceDisconnect}
              onSelectAccount={handleSourceSelection}
              isAuthenticating={oauthModal.isAuthenticating}
              authenticatingPlatform={oauthModal.platform}
              isEditMode={isEditMode}
            />
          </div>

        </div>
      )}

      {/* Step 2: Review & Finalize */}
      {currentStep === 2 && (
        <div className="max-w-[calc(100%-200px)] mx-auto mt-6 animate-in fade-in slide-in-from-bottom-4 duration-700 space-y-4 flex flex-col items-center ">
          <div className="grid md:grid-cols-2 gap-8 items-stretch relative">
            <SourceCard
              type="spending"
              platformName={spendingSummary.name}
              platformIcon={spendingSummary.icon || undefined}
              accounts={spendingAccounts}
              className="h-full"
            />

            {/* Connection Visualization */}
            <div className="hidden md:flex items-center justify-center absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
              <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center border-2 border-gray-100 shadow-xl">
                <div className="relative">
                  <Link size={24} className="text-primary-600" weight="bold" />
                  <div className="absolute inset-0 bg-primary-400 rounded-full animate-ping opacity-20" />
                </div>
              </div>
            </div>

            <SourceCard
              type="revenue"
              platformName={revenueSummary.name}
              platformIcon={revenueSummary.icon || undefined}
              accounts={revenueAccounts}
              className="h-full"
            />
          </div>
          <div className="p-4 space-y-4">
            <div className='mx-auto max-w-fit space-y-2'>
              <label className="block text-sm font-bold text-gray-800">{t('configurations.integrations.integrationPairName')}</label>
              <TextInput
                placeholder={t('configurations.integrations.pairNamePlaceholder')}
                value={integrationName}
                onChange={(e) => setIntegrationName(e.target.value)}
                sizing="md"
                className=" [&>div>input]:rounded-xl [&>div>input]:border-gray-200 [&>div>input]:focus:ring-primary-500 [&>div>input]:bg-white min-w-[400px]"
              />
              <p className="text-[11px] text-gray-500 mt-0.5">{t('configurations.integrations.pairNameDesc')}</p>
            </div>
          </div>
          {/* <ProTipBanner
            description={t("configurations.integrations.pairingDescription", "Pairing these accounts enables automatic Net Profit and ROI calculations across your dashboard. Data syncs every day.")}
            highlightedTerms={['Net Profit', 'ROI']}
          /> */}
        </div>
      )}

      {/* Sticky Navigation Footer */}
      <div className="absolute -bottom-2 left-0 right-0 bg-white/80 backdrop-blur-md border-t border-gray-200 z-30 w-[calc(100%+16px)] -ml-2 mt-4 ">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center justify-between">
            <Button
              color="light"
              size="md"
              onClick={() => currentStep === 1 ? navigate('/integration-pairs') : setCurrentStep(1)}
              className="text-gray-600 hover:text-gray-900 border-none bg-transparent hover:bg-gray-100"
            >
              <div className="flex items-center gap-2">
                <ArrowLeft size={18} />
                {currentStep === 1 ? t('configurations.integrations.backToPairs') : t('configurations.integrations.backToSources')}
              </div>
            </Button>

            {isEditMode ? (
              <Button
                color="light"
                size="md"
                onClick={() => setIsDeleteConfirmModalOpen(true)}
                disabled={isCancelling}
                className="text-red-500 hover:text-red-700 hover:bg-red-50 border-none bg-transparent"
              >
                <div className="flex items-center gap-2">
                  <Trash size={18} />
                  {t('configurations.integrations.deletePair')}
                </div>
              </Button>
            ) : (
              <Button
                color="light"
                size="md"
                onClick={() => setIsCancelConfirmModalOpen(true)}
                disabled={isCancelling}
                className="text-red-500 hover:text-red-700 hover:bg-red-50 border-none bg-transparent"
              >
                <div className="flex items-center gap-2">
                  <X size={18} />
                  {t('configurations.integrations.cancelIntegrationLabel')}
                </div>
              </Button>
            )}

          </div>

          {currentStep === 1 ? (
            <Button
              color="indigo"
              size="md"
              disabled={!step1Complete}
              onClick={() => setCurrentStep(2)}
              className="bg-primary-600 hover:bg-primary-700 text-white border-none shadow-lg px-6"
            >
              <div className="flex items-center gap-2">
                {t('configurations.integrations.continueToPairing')}
                <ArrowRight size={18} />
              </div>
            </Button>
          ) : (
            <Button
              size="md"
              color={'secondary'}
              onClick={handleFinalize}
              disabled={isSaving || !integrationName.trim()}
              isProcessing={isSaving}
              className="shadow-lg px-6"
              processingSpinner={<Spinner size="sm" color="success" />}
            >
              <div className="flex items-center gap-2">
                {!isSaving && <FloppyDiskBack size={18} />}
                {isEditMode ? t('configurations.integrations.updateIntegration') : t('configurations.integrations.finalizeIntegration')}
              </div>
            </Button>
          )}
        </div>
      </div>


      <AccountSelectionModal
        isOpen={accountModal.isOpen}
        onClose={() => setAccountModal(prev => ({ ...prev, isOpen: false }))}
        platform={accountModal.platform}
        accounts={accountModal.accounts}
        selectedAccountId={accountModal.selectedAccountId}
        onSelectAccount={handleAccountSelect}
        onConfirm={handleConfirmAccountSelection}
        isLoading={accountModal.isLoading}
        isConfirming={accountModal.isConfirming}
      />

      <AppleIAPModal
        isOpen={appleIAPModal.isOpen}
        onClose={() => setAppleIAPModal({ isOpen: false })}
        onConnect={handleAppleIAPConnect}
        onSuccess={handleAppleIAPSuccess}
      />

      <AppleAdsModal
        isOpen={appleAdsModal.isOpen}
        onClose={() => setAppleAdsModal({ isOpen: false, isSubmitting: false })}
        isSubmitting={appleAdsModal.isSubmitting}
        onSubmit={handleAppleAdsSubmit}
      />

      <GoogleIAPModal
        isOpen={googleIAPModal.isOpen}
        onClose={() => setGoogleIAPModal({ isOpen: false })}
        onConnect={handleGoogleIAPConnect}
        onSuccess={handleGoogleIAPSuccess}
      />

      {/* Disconnect Confirmation Modal */}
      <AnimatedModal
        isConfirmation
        show={cancelModal.isOpen}
        onClose={() => setCancelModal({ isOpen: false, platform: '' as PlatformType, email: '', socialAccountId: null, isDeleting: false })}
        onConfirm={handleDeleteSource}
        isConfirming={cancelModal.isDeleting}
        title={t('common.confirmation')}
        confirmText={t("configurations.integrations.disconnect")}
        confirmButtonColor="red"
        cancelText={t('common.cancel')}
      >
        <div className="text-gray-700 text-center">
          {t('configurations.integrations.disconnectSourceConfirm')}
        </div>
      </AnimatedModal>

      {/* No Accounts Warning Modal */}
      <AnimatedModal
        show={noAccountWarning.isOpen}
        onClose={() => setNoAccountWarning(prev => ({ ...prev, isOpen: false }))}
        closeButton={false}
        title=""
      >
        <div className="text-center py-3">
          <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-6">
            {noAccountWarning.warningType === 'missing_scope' ? (
              <Warning size={40} className="text-amber-500" weight="bold" />
            ) : (
              <MagnifyingGlass size={40} className="text-amber-500" weight="bold" />
            )}
          </div>
          <h3 className="text-2xl font-bold text-[#0f172a] mb-4">
            {noAccountWarning.warningType === 'missing_scope'
              ? t('configurations.integrations.missingScopeTitle')
              : t('configurations.integrations.findYourData')}
          </h3>
          <div className="text-sm text-gray-500 mb-10 leading-relaxed px-2">
            {noAccountWarning.warningType === 'missing_scope' ? (
              <Trans
                i18nKey="configurations.integrations.missingScopeBody"
                values={{ platform: noAccountWarning.platform === 'google_admob' ? 'Google AdMob' : 'Google Ads' }}
                components={{ bold: <span className="font-bold text-[#0f172a]" /> }}
              />
            ) : (
              <>
                {noAccountWarning.warningType === 'no_platform_account' ? (
                  <Trans
                    i18nKey="configurations.integrations.noAccountWarningEmail"
                    values={{ email: noAccountWarning.email || 'abc@xyz.com', platform: noAccountWarning.platform === 'google_admob' ? 'AdMob account' : noAccountWarning.platform === 'meta_ads' ? 'Meta Ads account' : noAccountWarning.platform === 'apple_search_ads' ? 'Apple Search Ads account' : 'Google Ads account' }}
                    components={{ bold: <span className="font-bold text-[#0f172a]" /> }}
                  />
                ) : (
                  <Trans
                    i18nKey="configurations.integrations.noAccountWarningAvailable"
                    values={{ email: noAccountWarning.email || 'abc@xyz.com', platform: noAccountWarning.platform === 'google_admob' ? 'AdMob account' : noAccountWarning.platform === 'meta_ads' ? 'Meta Ads account' : noAccountWarning.platform === 'apple_search_ads' ? 'Apple Search Ads account' : 'Google Ads account' }}
                    components={{ bold: <span className="font-bold text-[#0f172a]" /> }}
                  />
                )}
                {" "}{t('configurations.integrations.tryAgainDesc', { direction: noAccountWarning.platform === 'google_admob' ? t('configurations.integrations.revenue') : t('configurations.integrations.spend') })}
              </>
            )}
          </div>
          <div className="flex gap-4 w-full">
            <Button
              color="light"
              className='w-full'
              onClick={() => setNoAccountWarning(prev => ({ ...prev, isOpen: false }))}
            >
              {t('common.close')}
            </Button>
            <Button
              color="indigo"
              className='w-full'
              onClick={() => {
                setNoAccountWarning(prev => ({ ...prev, isOpen: false }));
                if (noAccountWarning.platform === 'apple_search_ads') {
                  setAppleAdsModal({ isOpen: true, isSubmitting: false });
                } else {
                  handleConnect(noAccountWarning.platform as 'google_ads' | 'google_admob' | 'meta_ads');
                }
              }}
            >
              <div className="flex items-center gap-2">
                <ArrowsClockwise size={18} weight="bold" />
                {noAccountWarning.warningType === 'missing_scope'
                  ? t('configurations.integrations.reAuth')
                  : t('configurations.integrations.tryAnotherEmail')}
              </div>
            </Button>

          </div>
        </div>
      </AnimatedModal>

      <AnimatedModal
        show={isCancelConfirmModalOpen || (blocker.state === 'blocked')}
        onClose={() => {
          setIsCancelConfirmModalOpen(false);
          if (blocker.state === 'blocked') {
            blocker.reset();
          }
        }}
        title={isEditMode ? t('configurations.integrations.leaveEditTitle', 'Leave Edit Integration') : t('configurations.integrations.cancelIntegrationTitle', 'Cancel Integration')}
        isConfirmation={true}
        onConfirm={() => {
          if (blocker.state === 'blocked') {
            blocker.proceed();
            clearEditState();
          } else {
            handleCancelIntegration();
          }
          setIsCancelConfirmModalOpen(false);
        }}
        confirmText={isEditMode ? t('common.yesLeave', 'Yes, Leave') : t('common.yesCancel', 'Yes, Cancel')}
        cancelText={isEditMode ? t('common.stay', 'Stay') : t('common.keepIntegration', 'Keep Integration')}
        isConfirming={isCancelling}
        confirmButtonColor="red"
      >
        <div className="text-center p-4">
          <p className="text-sm text-gray-600">
            {isEditMode
              ? t('configurations.integrations.leaveEditConfirmation', 'Are you sure you want to leave? All your current changes will be reverted.')
              : t('configurations.integrations.cancelIntegrationConfirmation', 'Are you sure you want to cancel this integration? All progress will be lost and pending connections will be deleted.')}
          </p>
          {integrationName && <p className="mt-2 font-bold text-gray-900">{integrationName}</p>}
        </div>
      </AnimatedModal>

      <AnimatedModal
        show={isDeleteConfirmModalOpen}
        onClose={() => setIsDeleteConfirmModalOpen(false)}
        title={t('configurations.integrations.deletePair')}
        isConfirmation={true}
        onConfirm={() => {
          handleDeleteIntegrationPair();
        }}
        confirmText={t("configurations.integrations.deletePair")}
        cancelText={t('common.cancel')}
        isConfirming={isCancelling}
        confirmButtonColor="red"
      >
        <div className="text-center p-4">
          <p className="text-sm text-gray-600">
            {t('configurations.integrations.deleteIntegrationConfirm', 'Are you sure you want to delete this integration pair? This action cannot be undone.')}
          </p>
          {integrationName && <p className="mt-2 font-bold text-gray-900">{integrationName}</p>}
        </div>
      </AnimatedModal>

      {/* Finalize Error Modal */}
      <AnimatedModal
        show={finalizeError.isOpen}
        onClose={() => setFinalizeError(prev => ({ ...prev, isOpen: false }))}
        title={t('common.something_went_wrong', 'Something went wrong')}
        closeButton={false}
      >
        <div className="py-2">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Warning size={32} className="text-red-500" weight="bold" />
          </div>
          <div className="text-center mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-1">
              {t('configurations.integrations.finalizeErrorTitle', 'Failed to Finalize Integration')}
            </h3>
            <p className="text-sm text-gray-500">
              {t('configurations.integrations.finalizeErrorDesc', 'The following sources encountered issues during registration. Please review and try again.')}
            </p>
          </div>

          <div className="bg-gray-50 rounded-xl p-4 mb-6 max-h-[200px] overflow-y-auto space-y-3 border border-gray-100">
            {finalizeError.errors.map((error, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <div className="h-2 w-2 rounded-full bg-red-400 mt-1.5 shrink-0" />
                <div>
                  <p className="text-sm font-bold text-gray-800">{error.platform}</p>
                  <p className="text-xs text-gray-500">{error.message}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-3 w-full">
            <Button
              color="light"
              className="w-full rounded-xl"
              onClick={() => {
                setFinalizeError(prev => ({ ...prev, isOpen: false }));
                setCurrentStep(1);
              }}
            >
              <div className="flex items-center gap-2">
                <Lightning size={18} weight="bold" className="text-amber-500" />
                {t('configurations.integrations.takeActions', 'Take Actions')}
              </div>
            </Button>
            <Button
              color="indigo"
              className="w-full rounded-xl shadow-lg shadow-indigo-100"
              onClick={handleFinalize}
            >
              <div className="flex items-center gap-2">
                <ArrowsClockwise size={18} weight="bold" />
                {t('common.retry', 'Retry')}
              </div>
            </Button>
          </div>
        </div>
      </AnimatedModal>


    </div>
  );
};

export default AddIntegration;
